import { useState, useEffect, useRef } from "react";
import Button from "@mui/material/Button";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router";
import CircularProgress from "@mui/material/CircularProgress";
import EnhancedTable from "components/utils/Table.js";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import EnrollmentService from "services/EnrollmentService.js";
import Chip from "@mui/material/Chip";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import { Outlet } from "react-router-dom";
import TableRowActionsCell from "components/utils/TableRowActionsCell.js";
import PopUpModal from "components/utils/PopUpModal.js";
import AuditFieldsModal from "components/modals/AuditFieldsModal.js";
import ConfirmActionModal from "components/modals/ConfirmActionModal";
import useAPI from "hooks/useAPI.js";
import DateUtils from "utils/DateUtils.js";
import { useOutletContext } from "react-router-dom";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";

export function EnrollmentSessions() {
  const [hospitalId] = useOutletContext();
  return <Outlet context={[hospitalId]} />;
}

export function EnrollmentSessionList() {
  let navigate = useNavigate();
  const [hospitalId] = useOutletContext();
  const { patientId } = useParams();
  const { enrollmentId } = useParams();
  const [tabValue, setTabValue] = useState("all");

  const [enrollmentSessions, setEnrollmentSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [auditFieldModalContent, setAuditFieldModalContent] = useState({
    createdAt: "",
    lastUpdatedAt: "",
    createdBy: "",
    lastUpdatedBy: "",
  });
  const auditFieldModalRef = useRef(null);
  const deleteSessionModalRef = useRef(null);
  const [deletingSessionId, setDeletingSessionId] = useState("");

  const getSessionsAPI = useAPI(
    EnrollmentService.getEnrollmentSessions,
    getSessionsHandler
  );
  const addAcademicAPI = useAPI(
    EnrollmentService.createEnrollmentSession,
    addSessionHandler
  );
  const addTherapyAPI = useAPI(
    EnrollmentService.createEnrollmentSession,
    addSessionHandler
  );
  const addNeurofeedbackAPI = useAPI(
    EnrollmentService.createEnrollmentSession,
    addSessionHandler
  );

  const deleteSessionAPI = useAPI(
    EnrollmentService.deleteEnrollmentSession,
    deleteSessionHandler,
    true
  );

  function getEnrollmentSessions() {
    getSessionsAPI.execute(hospitalId, patientId, enrollmentId);
  }

  function addAcademicSession() {
    addAcademicAPI.execute(hospitalId, patientId, enrollmentId, 'ACADEMIC');
  }

  function addTherapySession() {
    addTherapyAPI.execute(hospitalId, patientId, enrollmentId, 'THERAPY');
  }

  function addNeurofeedbackSession() {
    addNeurofeedbackAPI.execute(hospitalId, patientId, enrollmentId, 'NEUROFEEDBACK');
  }


  function deleteSession(sessionId) {
    deleteSessionAPI.execute(hospitalId, patientId, enrollmentId, sessionId);
  }

  function getSessionsHandler(response) {
    let data = response.data;
    let list = [];
    data.map((session, index) =>
      list.push(transformJsonData(session, index + 1))
    );
    setEnrollmentSessions(list);
    setFilteredSessions(list);
  }

  function addSessionHandler(response) {
    const newSession = transformJsonData(response.data, enrollmentSessions.length + 1);
    setEnrollmentSessions((prevState) => [...prevState, newSession]);
    setFilteredSessions((prevState) => [...prevState, newSession]);
  }

  function deleteSessionHandler(response) {
    getEnrollmentSessions();
  }

  useEffect(() => {
    getEnrollmentSessions();
  }, []);

  useEffect(() => {
    if (tabValue === "all") {
      setFilteredSessions(enrollmentSessions);
    } else {
      const filtered = enrollmentSessions.filter(
        (session) => JSON.stringify(session.type).toLowerCase().includes(tabValue)
      );
      // Renumber the serial numbers for the filtered sessions
      const renumberedFiltered = filtered.map((session, index) => ({
        ...session,
        index: index + 1
      }));
      setFilteredSessions(renumberedFiltered);
    }
  }, [tabValue, enrollmentSessions]);


  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Paper elevation={0} className="p-4">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Button onClick={() => navigate(-1)} variant="outlined">
          Cancel
        </Button>
        <Stack direction="row" spacing={2}>
          {addTherapyAPI.status === "pending" ? (
            <LoadingButton
              loading
              loadingPosition="start"
              startIcon={<img src="assets/plus-icon.svg" alt="Add icon" />}
              variant="contained"
            >
              Therapy
            </LoadingButton>
          ) : (
            <Button
              onClick={addTherapySession}
              startIcon={<img src="assets/plus-icon.svg" alt="Add icon" />}
              variant="contained"
            >
              Therapy
            </Button>
          )}
          {addAcademicAPI.status === "pending" ? (
            <LoadingButton
              loading
              loadingPosition="start"
              startIcon={<img src="assets/plus-icon.svg" alt="Add icon" />}
              variant="contained"
            >
              Academic
            </LoadingButton>
          ) : (
            <Button
              onClick={addAcademicSession}
              startIcon={<img src="assets/plus-icon.svg" alt="Add icon" />}
              variant="contained"
            >
              Academic
            </Button>
          )}
          {addNeurofeedbackAPI.status === "pending" ? (
            <LoadingButton
              loading
              loadingPosition="start"
              startIcon={<img src="assets/plus-icon.svg" alt="Add icon" />}
              variant="contained"
            >
              Neurofeedback
            </LoadingButton>
          ) : (
            <Button
              onClick={addNeurofeedbackSession}
              startIcon={<img src="assets/plus-icon.svg" alt="Add icon" />}
              variant="contained"
            >
              Neurofeedback
            </Button>
          )}
        </Stack>
      </Stack>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 3, mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="session types">
          <Tab label="All Sessions" value="all" />
          <Tab label="Academics" value="academic" />
          <Tab label="Therapy" value="therapy" />
          <Tab label="Neurofeedback" value="neurofeedback" />
        </Tabs>
      </Box>

      {getSessionsAPI.status === "pending" ? (
        <div className="text-center">
          <CircularProgress
            className="text-center"
            sx={{
              my: 20,
            }}
          />
        </div>
      ) : (
        <EnhancedTable
          headings={tableHeadings}
          rowsData={filteredSessions}
          rowsPerPage={10}
        />
      )}
      <PopUpModal ref={auditFieldModalRef} modalWidth={320}>
        <AuditFieldsModal data={auditFieldModalContent} />
      </PopUpModal>
      <PopUpModal ref={deleteSessionModalRef} modalWidth={400}>
        <ConfirmActionModal
          content="Do you want to confirm deleting the session?"
          confirmActionHandler={() => {
            deleteSession(deletingSessionId);
            deleteSessionModalRef.current.closeModal();
          }}
          onCloseHandler={() => deleteSessionModalRef.current.closeModal()}
        />
      </PopUpModal>
    </Paper>
  );

  function transformJsonData(session, index) {
    const isGeneratedFromStimForm = session.metadata?.generated_from_stim_form === true;

    return {
      id: session.id,
      index,
      type: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {session.type}
          {isGeneratedFromStimForm && (
            <Chip
              label="Stim Form"
              size="small"
              color="info"
              variant="outlined"
              style={{ fontSize: '0.75rem' }}
            />
          )}
        </div>
      ),
      createdAt: DateUtils.formatDateTime(session.createdAt),
      status: getStatusChip(session.isCompleted),
      actions: <ActionsCell session={session} />,
    };
  }

  function ActionsCell({ session }) {
    const actions = {
      startItems: [
        {
          component: (
            <InfoOutlinedIcon fontSize="inherit" color="custom.light" />
          ),
          onClickHandler: () => {
            setAuditFieldModalContent({
              createdAt: DateUtils.formatDateTime(session.createdAt),
              lastUpdatedAt: DateUtils.formatDateTime(session.lastUpdatedAt),
              createdBy: session.createdByUserFullName,
              lastUpdatedBy: session.lastUpdatedByUserFullName,
            });
            auditFieldModalRef.current.openModal();
          },
        },
        {
          component: <VisibilityIcon fontSize="inherit" color="secondary" />,
          onClickHandler: () => {
            if (session.type === "Therapy") {
              navigate(`therapy/${session.id}?isCompleted=${session.isCompleted}`);
            }
            else if (session.type === "Academic") {
              navigate(`academic/${session.id}?isCompleted=${session.isCompleted}`);
            }
            else if (session.type === "Neurofeedback") {
              navigate(`neurofeedback/${session.id}?isCompleted=${session.isCompleted}`);
            }
          },
        },
        {
          component: <DeleteOutlineIcon fontSize="inherit" color="error" />,
          onClickHandler: () => {
            setDeletingSessionId(session.id);
            deleteSessionModalRef.current.openModal();
          },
        },
      ],
      menuItems: [],
    };

    return <TableRowActionsCell actions={actions} />;
  }
}

function getStatusChip(isCompleted) {
  let color = "warning";
  let label = "Not Completed";
  if (isCompleted) {
    color = "success";
    label = "Completed";
  }
  return <Chip label={label} color={color} />;
}

const tableHeadings = [
  { id: "index", label: "S.No." },
  { id: "type", label: "Type" },
  { id: "createdAt", label: "Created At" },
  { id: "status", label: "Status" },
  { id: "actions", label: "Actions" },
];